import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Mail, CheckCircle, XCircle, Activity, ArrowRight } from '../../icons/wp-icons';
import { kiZentraleApi } from '../../api/kiZentraleApi';
import { useEmbed } from '../../hooks/useEmbed';
import KiTodos from './KiTodos';

function WhatsAppIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
}

export default function KiUebersicht({ onNavigate }) {
  const { isEmbed } = useEmbed();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  // Animation State
  const [clickedCard, setClickedCard] = useState(null);
  const [showOverlay, setShowOverlay] = useState(false);
  const [transitionData, setTransitionData] = useState(null);
  const cardRefs = useRef({});

  useEffect(() => {
    loadStatus();
    const interval = setInterval(loadStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadStatus = async () => {
    try {
      const data = await kiZentraleApi.getStatus();
      setStatus(data);
    } catch (err) {
      console.error('Status laden fehlgeschlagen:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = (agent) => {
    const cardElement = cardRefs.current[agent.key];
    if (cardElement) {
      const rect = cardElement.getBoundingClientRect();
      // Zielposition: vertikal zentriert im Viewport (wie Kundenkarten)
      const expandedHeight = 180; // geschätzte Höhe der expandierten Karte
      const targetTop = Math.round((window.innerHeight - expandedHeight) / 2);

      // Step 1: Card hebt sich an
      setClickedCard(agent.key);

      // Step 2: Overlay zeigen und Expand-Animation starten
      setTimeout(() => {
        setShowOverlay(true);
        setTransitionData({ agent, cardRect: rect, targetTop });

        // Step 3: Zum Tab navigieren
        setTimeout(() => {
          onNavigate(agent.tab);
        }, 250);
      }, 50);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!status) return null;

  const agents = [
    {
      key: 'telefon',
      label: 'Telefon-Agent',
      icon: Phone,
      color: 'purple',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-700',
      borderColor: 'border-purple-200 hover:border-purple-400',
      data: status.telefon,
      tab: 'telefon',
      detail: isEmbed
        ? (status.telefon.phoneNumber ? '•••• ••• ••••' : 'Keine Nummer')
        : (status.telefon.phoneNumber || 'Keine Nummer'),
      countLabel: 'Anrufe heute',
    },
    {
      key: 'whatsapp',
      label: 'WhatsApp-Agent',
      icon: WhatsAppIcon,
      color: 'green',
      bgColor: 'bg-green-50',
      textColor: 'text-green-700',
      borderColor: 'border-green-200 hover:border-green-400',
      data: status.whatsapp,
      tab: 'whatsapp',
      detail: isEmbed
        ? (status.whatsapp.phoneNumber ? '•••• ••• ••••' : 'Keine Nummer')
        : (status.whatsapp.phoneNumber || 'Keine Nummer'),
      countLabel: 'Nachrichten heute',
    },
    {
      key: 'email',
      label: 'E-Mail-Agent',
      icon: Mail,
      color: 'blue',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-700',
      borderColor: 'border-blue-200 hover:border-blue-400',
      data: status.email,
      tab: 'email',
      detail: (() => {
        if (!status.email.providerEmail) return status.email.enabled ? 'Aktiv' : 'Inaktiv';
        const providerLabel = { gmail: 'Gmail', outlook: 'Outlook', imap: 'IMAP/SMTP' }[status.email.provider] || status.email.provider;
        return isEmbed ? providerLabel : `${providerLabel}: ${status.email.providerEmail}`;
      })(),
      countLabel: 'E-Mails heute',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Agent Status Cards */}
      <motion.div
        className="grid grid-cols-1 md:grid-cols-3 gap-4"
        animate={{ opacity: showOverlay ? 0 : 1 }}
        transition={{ duration: 0.25 }}
      >
        {agents.map((agent) => {
          const Icon = agent.icon;
          const isActive = agent.data.enabled;
          const isConnected = agent.data.connected;

          return (
            <motion.div
              key={agent.key}
              ref={(el) => cardRefs.current[agent.key] = el}
              className={`bg-surface rounded-card shadow-card border ${agent.borderColor} p-5 cursor-pointer ${
                clickedCard === agent.key ? 'invisible' : ''
              }`}
              onClick={() => handleCardClick(agent)}
              whileHover={{
                scale: 1.02,
                boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
              }}
              whileTap={{
                scale: 0.98,
              }}
              animate={{
                scale: clickedCard === agent.key ? 1.05 : 1,
                boxShadow: clickedCard === agent.key
                  ? '0 20px 60px rgba(0,0,0,0.2)'
                  : '0 4px 20px rgba(0,0,0,0.08)',
              }}
              transition={{
                type: 'spring',
                stiffness: 400,
                damping: 25,
              }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`p-2.5 rounded-xl ${agent.bgColor}`}>
                  <Icon className={`w-5 h-5 ${agent.textColor}`} />
                </div>
                <div className="flex items-center gap-1.5">
                  {isActive && isConnected ? (
                    <>
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-xs font-medium text-green-600">Aktiv</span>
                    </>
                  ) : isActive ? (
                    <>
                      <Activity className="w-4 h-4 text-yellow-500" />
                      <span className="text-xs font-medium text-yellow-600">Verbindung...</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4 text-gray-400" />
                      <span className="text-xs font-medium text-gray-500">Inaktiv</span>
                    </>
                  )}
                </div>
              </div>

              <h3 className="font-semibold text-text mb-1">{agent.label}</h3>
              <p className="text-sm text-text-muted mb-3">{agent.detail}</p>

              <div className="flex items-center justify-between pt-3 border-t border-border">
                <div>
                  <span className="text-2xl font-bold text-text">{agent.data.todayCount}</span>
                  <span className="text-xs text-text-muted ml-1.5">{agent.countLabel}</span>
                </div>
                <ArrowRight className="w-4 h-4 text-text-muted" />
              </div>
            </motion.div>
          );
        })}
      </motion.div>

      {/* Expand Animation Overlay */}
      <AnimatePresence>
        {showOverlay && transitionData && (
          <motion.div
            className="fixed inset-0 z-50 pointer-events-none"
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            {/* Background */}
            <motion.div
              className="absolute inset-0 bg-background"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.15 }}
            />

            {/* Expanding Card */}
            <motion.div
              className={`absolute bg-surface rounded-card shadow-2xl overflow-hidden border ${transitionData.agent.borderColor.split(' ')[0]}`}
              initial={{
                top: transitionData.cardRect.top,
                left: transitionData.cardRect.left,
                width: transitionData.cardRect.width,
                height: transitionData.cardRect.height,
                borderRadius: 16,
              }}
              animate={{
                top: transitionData.targetTop,
                left: '50%',
                x: '-50%',
                width: 'min(calc(100vw - 32px), 900px)',
                height: 'auto',
                borderRadius: 16,
              }}
              transition={{
                duration: 0.25,
                ease: [0.32, 0.72, 0, 1],
              }}
            >
              {(() => {
                const agent = transitionData.agent;
                const Icon = agent.icon;
                const isActive = agent.data.enabled;
                const isConnected = agent.data.connected;

                return (
                  <div className="p-6" style={{ minHeight: '140px' }}>
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`p-3 rounded-xl ${agent.bgColor}`}>
                          <Icon className={`w-6 h-6 ${agent.textColor}`} />
                        </div>
                        <div>
                          <h1 className="text-xl font-bold text-text">{agent.label}</h1>
                          <p className="text-sm text-text-muted">{agent.detail}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {isActive && isConnected ? (
                          <>
                            <CheckCircle className="w-5 h-5 text-green-500" />
                            <span className="text-sm font-medium text-green-600">Aktiv</span>
                          </>
                        ) : isActive ? (
                          <>
                            <Activity className="w-5 h-5 text-yellow-500" />
                            <span className="text-sm font-medium text-yellow-600">Verbindung...</span>
                          </>
                        ) : (
                          <>
                            <XCircle className="w-5 h-5 text-gray-400" />
                            <span className="text-sm font-medium text-gray-500">Inaktiv</span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Stats */}
                    <motion.div
                      className="pt-4 border-t border-border"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.15, duration: 0.2 }}
                    >
                      <span className="text-3xl font-bold text-text">{agent.data.todayCount}</span>
                      <span className="text-sm text-text-muted ml-2">{agent.countLabel}</span>
                    </motion.div>
                  </div>
                );
              })()}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* KI Todos */}
      <KiTodos />

      {/* Quick Actions */}
      <div className="bg-surface rounded-card shadow-card p-5">
        <h3 className="font-semibold text-text mb-4">Schnellzugriff</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <button
            onClick={() => onNavigate('einstellungen')}
            className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-gray-50 transition text-left"
          >
            <div className="p-2 bg-primary/10 rounded-lg">
              <Activity className="w-4 h-4 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium">Autonomiestufe anpassen</p>
              <p className="text-xs text-text-muted">Gilt für alle Agenten</p>
            </div>
          </button>
          <button
            onClick={() => onNavigate('einstellungen')}
            className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-gray-50 transition text-left"
          >
            <div className="p-2 bg-primary/10 rounded-lg">
              <CheckCircle className="w-4 h-4 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium">Pflichtfelder konfigurieren</p>
              <p className="text-xs text-text-muted">Was muss abgefragt werden</p>
            </div>
          </button>
          <button
            onClick={() => onNavigate('protokoll')}
            className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-gray-50 transition text-left"
          >
            <div className="p-2 bg-primary/10 rounded-lg">
              <Mail className="w-4 h-4 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium">Alle Protokolle ansehen</p>
              <p className="text-xs text-text-muted">Anrufe, Chats & E-Mails</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}
